<script>
  import other from '../functions-common/other';
  import math from '../functions-common/math';
  import date from '../functions-common/date';
  import Map from './Map.svelte'
  import {slide} from 'svelte/transition'
  import Score from './Score.svelte'
  import Attempts from './Attempts.svelte'

  const substituteVars = other.substituteVars;

  export let globalLeaderboard;
  export let categoryLeaderboard;
  export let types;
  export let scores;
  export let live;
  export let loading = false;

  let openedPlayerScores = [];
  let openedAttempts = [];

  let selectedType = 'global';

  let accType = 'total';

  function onTypeSelected(type) {
    selectedType = type;
  }

  function toggleScores(playerId) {
    if (openedPlayerScores.includes(playerId))
      openedPlayerScores = openedPlayerScores.filter(pId => pId !== playerId);
    else
      openedPlayerScores = openedPlayerScores.concat([playerId]);
  }

  const getAttemptKey = (playerId, mapId) => `${playerId}:${mapId}`;

  function toggleAttempts(playerId, mapId) {
    const key = getAttemptKey(playerId, mapId);

    if (openedAttempts.includes(key))
      openedAttempts = openedAttempts.filter(k => k !== key);
    else
      openedAttempts = openedAttempts.concat([key]);
  }

  function formatLeaderboard(leaderboard, allScores, type, accType) {
    if (!leaderboard) return null;

    return leaderboard.map(player => {
      const maps_passed_percent = player.maps_cnt && player.maps_passed ? player.maps_passed / player.maps_cnt * 100 : 3;

      let progressClass = '';
      switch (true) {
        case maps_passed_percent >= 100:
          progressClass = 'is-success';
          break;
        case maps_passed_percent >= 75:
          progressClass = 'is-primary';
          break;
        case maps_passed_percent >= 50:
          progressClass = 'is-warning';
          break;
        default:
          progressClass = 'is-error';
          break;
      }

      const scores = allScores
        ? allScores
          .filter(score => score.player_id === player.player_id && (!type || score.category_name === type))
          .map(score => {
            const acc_fmt = score.acc ? `${math.formatNumber(score.acc)}%` : '-';
            const score_fmt = score.score ? math.formatNumber(score.score, 0) : '-';
            const fc_fmt = score.fc ? 'FC' : '';
            const started_at_fmt = score.started_at ? date.formatDate(date.dateFromString(score.started_at)) : '';

            return {...score, acc_fmt, score_fmt, fc_fmt, started_at_fmt};
          })
        : null;

      return {
        ...player,
        acc: player.avg_acc,
        score: player.total_score,
        maps_passed_percent,
        progressClass,
        scores,
      };
    })
      .sort((a,b) => accType === 'total' ? (b?.acc ?? 0) - (a?.acc ?? 0) : (b?.avg_passed_acc ?? 0) - (a?.avg_passed_acc ?? 0));
  }

  function onTypesOrSelectedUpdated() {
    if (!types || !Array.isArray(types)) return;

    if (!types.find(t => t.type === selectedType)) selectedType = types.length ? types[0].type : null;
  }

  function getLeaderboard() {
    if (!currentType) return null;

    if (currentType.type === 'global') return globalLeaderboard;
    else return categoryLeaderboard.filter(cp => cp.category_name === currentType.type)
  }

  $: onTypesOrSelectedUpdated(types, selectedType);
  $: currentType = types && selectedType ? types.find(t => t.type === selectedType) : null;
  $: leaderboard = getLeaderboard(categoryLeaderboard, currentType);
  $: leaderboardFormatted = formatLeaderboard(leaderboard, scores, !selectedType || selectedType === 'global' ? null : selectedType, accType);
</script>

<h3>
  {#each types as type}
    <button type="button" class={`nes-btn ${type.type === selectedType ? 'is-primary' : 'is-default'}`}
            on:click={() => onTypeSelected(type.type)}>{type.name}</button>
  {/each}
</h3>

<div class="acc-type">
  <label>
    <input type="radio" class="nes-radio is-dark" bind:group={accType} checked={accType === 'total'} value="total" />
    <span>Total Acc</span>
  </label>

  <label>
    <input type="radio" class="nes-radio is-dark" bind:group={accType} checked={accType === 'played'} value="played" />
    <span>Passed maps Acc</span>
  </label>
</div>

{#if leaderboardFormatted && leaderboardFormatted.length}
  {#each leaderboardFormatted as player, idx}
    <Score score={player} withDetails={true} single={false} live={!!live[player.player_id]} opened={openedPlayerScores.includes(player.player_id)}
           on:show-details={() => toggleScores(player.player_id)} {accType}
    />

    {#if player.scores && player.scores.length && openedPlayerScores.includes(player.player_id)}
      <section class="scores" transition:slide={{duration: 500}}>
        {#each player.scores as score}
          <Map map={score} live={!!(live[player.player_id] && live[player.player_id][score.map_id])}
               on:click={() => score.score ? toggleAttempts(score.player_id, score.map_id) : null}
          >
            <span class="rank-and-attempt" slot="header">
              <span title="rank">{score.rank ? `#${score.rank}` : '-'}</span>
              <span
                class="tablet-and-up-only"
                title={score.started_at ? `Attempt no. ${score.attempt_num} at ${score.started_at_fmt}`: ''}>{score.attempt_num ? `(${score.attempt_num})` : ''}</span>
              <span class="acc mobile-only">{score.acc_fmt}</span>
              {#if score.fc}<span class="fc mobile-only">{score.fc_fmt}</span>{/if}
            </span>

            <span class="map-footer" slot="footer">
              <span class="fc tablet-and-up-only">{score.fc_fmt}</span>

              <span class="acc tablet-and-up-only">{score.acc_fmt}</span>

              <span class="score tablet-and-up-only">{score.score_fmt}</span>

              {#if score.score}
                <span class="more" title="Show/hide attempts">
                  <span class="chevron">
                    <img src={"/assets/images/" + (openedAttempts.includes(getAttemptKey(score.player_id, score.map_id)) ?
                    'chevron-down-black' : 'chevron-right-black') + ".png"}/>
                  </span>
                </span>
              {/if}
            </span>
          </Map>
          {#if openedAttempts.includes(getAttemptKey(score.player_id, score.map_id))}
            <Attempts projectId={score.project_id} playerId={score.player_id} mapId={score.map_id} />
          {/if}
        {/each}
      </section>
    {/if}
  {/each}
{:else if !loading}
  <p>It looks like leaderboard doesn't exist yet.</p>
{/if}

<style>
    h3 {
        margin-bottom: 2rem;
        text-align: center;
    }

    @media screen and (max-width: 767px) {
        h3 {
            text-align: center;
        }
    }

    .scores {
        display: block;
        color: #222;
        background-color: #aaa;
        padding: .5rem;
        margin-bottom: 2rem;
    }

    .scores :global(.map-score) {
        cursor: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAzElEQVRYR+2X0Q6AIAhF5f8/2jYXZkwEjNSVvVUjDpcrGgT7FUkI2D9xRfQETwNIiWO85wfINfQUEyxBG2ArsLwC0jioGt5zFcwF4OYDPi/mBYKm4t0U8ATgRm3ThFoAqkhNgWkA0jJLvaOVSs7j3qMnSgXWBMiWPXe94QqMBMBc1VZIvaTu5u5pQewq0EqNZvIEMCmxAawK0DNkay9QmfFNAJUXfgGgUkLaE7j/h8fnASkxHTz0DGIBMCnBeeM7AArpUd3mz2x3C7wADglA8BcWMZhZAAAAAElFTkSuQmCC) 14 0, pointer;
    }

    .scores .map-footer {
        display: contents;
    }

    .scores .map-footer > * {
        width: max-content;
        max-width: 100%;
        margin-right: .5rem;
    }

    .scores .acc {
        width: 6.5rem!important;
    }

    .rank-and-attempt {
        width: 2rem !important;
    }

    .more {
        position: relative;
        top: -2px;
        width: 1.25rem !important;
        height: 1.25rem;
        cursor: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAzElEQVRYR+2X0Q6AIAhF5f8/2jYXZkwEjNSVvVUjDpcrGgT7FUkI2D9xRfQETwNIiWO85wfINfQUEyxBG2ArsLwC0jioGt5zFcwF4OYDPi/mBYKm4t0U8ATgRm3ThFoAqkhNgWkA0jJLvaOVSs7j3qMnSgXWBMiWPXe94QqMBMBc1VZIvaTu5u5pQewq0EqNZvIEMCmxAawK0DNkay9QmfFNAJUXfgGgUkLaE7j/h8fnASkxHTz0DGIBMCnBeeM7AArpUd3mz2x3C7wADglA8BcWMZhZAAAAAElFTkSuQmCC) 14 0, pointer;
    }

    .chevron{
        position: relative;
        width: 14px;
        height: 14px;
    }

    .acc-type {
        background-color:#212529;
        padding: 1rem 0;
        text-align: right;
    }

    @media screen and (max-width: 767px) {
        .rank-and-attempt {
            display: flex;
            justify-content: space-evenly;
            width: 100%!important;
            margin-bottom: .5rem;
        }

        .acc-type {
            text-align: center;
        }
    }
</style>